import { useEffect, useState } from "react";

/**
 * Cookie consent banner.
 *
 * Integration notes:
 * 1. Drop this file into your components directory (e.g. src/components/CookieConsent.jsx)
 *    and mount it once near the root of the app, e.g. in App.js:
 *
 *      import CookieConsent from "./components/CookieConsent";
 *      ...
 *      <CookieConsent />
 *      <Routes>...</Routes>
 *
 * 2. To reopen preferences later (e.g. a "Cookie Settings" link in your footer),
 *    call the exported helper:
 *
 *      import { openCookieSettings } from "./components/CookieConsent";
 *      <button onClick={openCookieSettings}>Cookie Settings</button>
 *
 * 3. Before loading any analytics or advertising script (Google Analytics,
 *    a future Google Ads tag, Meta Pixel, etc.), gate it behind the exported
 *    hasConsent() helper so nothing fires before the visitor has chosen:
 *
 *      import { hasConsent } from "./components/CookieConsent";
 *      if (hasConsent("analytics")) {
 *        // load GA script here
 *      }
 *
 *    Scripts should also listen for the "cookieConsentChanged" window event
 *    so they can load the moment a visitor accepts, without a page refresh:
 *
 *      window.addEventListener("cookieConsentChanged", (e) => {
 *        if (e.detail.analytics) { // load GA script here }
 *      });
 *
 * 4. This uses localStorage directly since it ships to your production site,
 *    not a sandboxed preview environment.
 */

const STORAGE_KEY = "aj_cookie_consent";
const CONSENT_VERSION = 1;

const CATEGORIES = [
  {
    key: "essential",
    label: "Essential",
    description:
      "Required for the Site to function, such as remembering your cookie preference. Always on.",
    locked: true,
  },
  {
    key: "analytics",
    label: "Analytics",
    description:
      "Helps understand how visitors use the Site, so it can be improved over time.",
    locked: false,
  },
  {
    key: "marketing",
    label: "Advertising",
    description:
      "Used for remarketing and personalized ads, if and when such tags are added to the Site.",
    locked: false,
  },
];

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(choices) {
  const payload = {
    version: CONSENT_VERSION,
    choices,
    timestamp: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage unavailable (private browsing, etc.); consent will be
    // requested again next visit, which is the safe fallback.
  }
  window.dispatchEvent(
    new CustomEvent("cookieConsentChanged", { detail: choices })
  );
}

/** Returns true if the visitor has consented to a given category. */
export function hasConsent(category) {
  if (category === "essential") return true;
  const stored = readStoredConsent();
  return Boolean(stored?.choices?.[category]);
}

/** Reopens the banner in customize mode, e.g. from a footer link. */
export function openCookieSettings() {
  window.dispatchEvent(new Event("openCookieSettings"));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [choices, setChoices] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) {
      setVisible(true);
    } else {
      setChoices({
        analytics: Boolean(stored.choices?.analytics),
        marketing: Boolean(stored.choices?.marketing),
      });
    }

    const reopen = () => {
      const current = readStoredConsent();
      setChoices({
        analytics: Boolean(current?.choices?.analytics),
        marketing: Boolean(current?.choices?.marketing),
      });
      setExpanded(true);
      setVisible(true);
    };

    window.addEventListener("openCookieSettings", reopen);
    return () => window.removeEventListener("openCookieSettings", reopen);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    const all = { analytics: true, marketing: true };
    setChoices(all);
    writeConsent(all);
    setVisible(false);
  };

  const rejectNonEssential = () => {
    const none = { analytics: false, marketing: false };
    setChoices(none);
    writeConsent(none);
    setVisible(false);
  };

  const savePreferences = () => {
    writeConsent(choices);
    setVisible(false);
  };

  const toggle = (key) => {
    setChoices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      aria-modal="false"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-[#000d14]/10 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90"
    >
      <div className="mx-auto max-w-6xl px-6 py-5">
        {!expanded ? (
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <p className="max-w-2xl text-sm leading-relaxed text-[#0f1720]/80">
              This site uses cookies for essential functionality and,
              with your consent, for analytics and future advertising
              features. Read the{" "}
              <a
                href="/privacy-policy"
                className="underline underline-offset-2 hover:text-[#000d14]"
              >
                Privacy Policy
              </a>{" "}
              to learn more.
            </p>
            <div className="flex flex-shrink-0 flex-wrap items-center gap-3">
              <button
                onClick={() => setExpanded(true)}
                className="rounded-[12px] px-4 py-2 text-sm font-medium text-[#0f1720]/70 transition-colors hover:text-[#000d14]"
              >
                Customize
              </button>
              <button
                onClick={rejectNonEssential}
                className="rounded-[12px] border border-[#000d14]/15 px-4 py-2 text-sm font-medium text-[#0f1720] transition-colors hover:border-[#000d14]/30"
              >
                Reject non-essential
              </button>
              <button
                onClick={acceptAll}
                className="rounded-[12px] bg-[#000d14] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-[#000d14]">
                  Cookie preferences
                </h2>
                <p className="mt-1 text-sm text-[#0f1720]/70">
                  Choose which categories of cookies you're comfortable
                  with. You can change this anytime.
                </p>
              </div>
              <button
                onClick={() => setExpanded(false)}
                aria-label="Close"
                className="text-sm text-[#0f1720]/50 hover:text-[#0f1720]/80"
              >
                Close
              </button>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-3">
              {CATEGORIES.map((category) => (
                <div
                  key={category.key}
                  className="rounded-[12px] border border-[#000d14]/10 p-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#0f1720]">
                      {category.label}
                    </span>
                    <Toggle
                      checked={
                        category.locked ? true : choices[category.key]
                      }
                      disabled={category.locked}
                      onChange={() => toggle(category.key)}
                    />
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-[#0f1720]/60">
                    {category.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
              <button
                onClick={rejectNonEssential}
                className="rounded-[12px] border border-[#000d14]/15 px-4 py-2 text-sm font-medium text-[#0f1720] transition-colors hover:border-[#000d14]/30"
              >
                Reject non-essential
              </button>
              <button
                onClick={savePreferences}
                className="rounded-[12px] bg-[#000d14] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Toggle({ checked, disabled, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative h-5 w-9 rounded-full transition-colors ${
        checked ? "bg-[#000d14]" : "bg-[#000d14]/15"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
